import React, { useMemo } from "react";
import {
  Box,
  Breadcrumbs,
  Card,
  CircularProgress,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useParams } from "react-router-dom";
import { format, differenceInDays } from "date-fns";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useGetDealQuery } from "Services/deal";
import { useGetPipelinesQuery } from "Services/pipeline";
import { useAppSelector } from "State/hooks";

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "-";
  try {
    return format(new Date(value), "MM/dd/yyyy");
  } catch {
    return "-";
  }
};

const formatCurrency = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === "") return "-";
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return "-";
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
};

const formatDays = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return "-";
  return `${value} day${value === 1 ? "" : "s"}`;
};

const daysAgo = (isoDate: string | null | undefined): number | null => {
  if (!isoDate) return null;
  try {
    return differenceInDays(new Date(), new Date(isoDate));
  } catch {
    return null;
  }
};

interface InfoFieldProps {
  label: string;
  value: string;
}

function InfoField({ label, value }: InfoFieldProps) {
  return (
    <Box sx={{ minWidth: 160 }}>
      <Typography sx={{ fontSize: "0.75rem", color: "#6B7280", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.875rem", color: "#111827", fontWeight: 500 }}>
        {value}
      </Typography>
    </Box>
  );
}

export default function DealDetail() {
  const { dealId } = useParams<{ dealId: string }>();
  const companyId = useAppSelector((state) => state.company.companyId);

  const { data: deal, isLoading, isError } = useGetDealQuery(
    { companyId, dealId: dealId ?? "" },
    { skip: !dealId || !companyId }
  );

  const { data: pipelines } = useGetPipelinesQuery(companyId, { skip: !companyId });

  const stageIdToLabel = useMemo(() => {
    try {
      const cached = localStorage.getItem("stageValueToLabel");
      if (cached) return new Map<string, string>(Object.entries(JSON.parse(cached)));
    } catch {}
    const map = new Map<string, string>();
    pipelines?.forEach((pipeline) => {
      pipeline.stages.forEach((stage) => {
        if (stage.value) map.set(stage.value, stage.label);
      });
    });
    return map;
  }, [pipelines]);

  const pipelineIdToLabel = useMemo(() => {
    const map = new Map<string, string>();
    pipelines?.forEach((pipeline) => {
      map.set(pipeline.sourcePipelineId, pipeline.label);
    });
    return map;
  }, [pipelines]);

  const resolveStage = (id: string | null | undefined) =>
    id ? (stageIdToLabel.get(id) ?? id) : "-";

  const p = deal?.properties;
  const dealName = p?.dealname || dealId || "Deal Details";
  const daysInCurrentStage = daysAgo(p?.hs_v2_date_entered_current_stage);
  const timeSinceLastActivity = daysAgo(p?.notes_last_contacted);
  const totalCalls =
    (Number(p?.hs_number_of_inbound_calls) || 0) +
    (Number(p?.hs_number_of_outbound_calls) || 0);

  const stageHistory = deal?.propertiesWithHistory?.dealstage ?? [];

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: 3 }}
      >
        <MuiLink
          component={RouterLink}
          to="/home"
          underline="hover"
          sx={{ fontSize: "0.875rem", color: "#6B7280" }}
        >
          Home
        </MuiLink>
        <Typography sx={{ fontSize: "0.875rem", color: "#111827" }}>
          {dealName}
        </Typography>
      </Breadcrumbs>

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {isError && (
        <Typography color="error" sx={{ mt: 2 }}>
          Failed to load deal details. Please try again.
        </Typography>
      )}

      {deal && (
        <>
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
            {dealName}
          </Typography>

          {/* Deal summary */}
          <Card variant="outlined" sx={{ p: 3, mb: 3, bgcolor: "#FFFFFF" }}>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              sx={{ mb: 2, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.05em" }}
            >
              Deal Summary
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
              <InfoField label="Stage" value={resolveStage(p?.dealstage)} />
              <InfoField
                label="Pipeline"
                value={p?.pipeline ? (pipelineIdToLabel.get(p.pipeline) ?? p.pipeline) : "-"}
              />
              <InfoField label="Amount" value={formatCurrency(p?.amount)} />
              <InfoField label="MRR" value={formatCurrency(p?.deal_mrr)} />
              <InfoField label="Create Date" value={formatDate(p?.createdate)} />
              <InfoField label="Close Date" value={formatDate(p?.closedate)} />
              <InfoField label="Days in Current Stage" value={formatDays(daysInCurrentStage)} />
              <InfoField label="Deal Type" value={p?.dealtype || "-"} />
              <InfoField label="Channel of Lead" value={p?.channel_of_lead || "-"} />
            </Box>
          </Card>

          {/* Activity stats */}
          <Card variant="outlined" sx={{ p: 3, mb: 3, bgcolor: "#FFFFFF" }}>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              sx={{ mb: 2, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.05em" }}
            >
              Activity
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
              <InfoField label="Contacts" value={p?.num_associated_contacts || "0"} />
              <InfoField label="Calls" value={String(totalCalls)} />
              <InfoField label="Meetings" value={p?.hs_number_of_scheduled_meetings || "0"} />
              <InfoField label="Notes" value={p?.num_notes || "0"} />
              <InfoField label="Overdue Tasks" value={p?.hs_number_of_overdue_tasks || "0"} />
              <InfoField
                label="Time Since Last Activity"
                value={formatDays(timeSinceLastActivity)}
              />
            </Box>
          </Card>

          {/* Stage history */}
          {stageHistory.length > 0 && (
            <Card variant="outlined" sx={{ p: 3, bgcolor: "#FFFFFF" }}>
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                sx={{ mb: 2, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.05em" }}
              >
                Stage History
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ borderBottom: "none", pb: 1 }}>
                        <Typography sx={{ fontSize: "0.75rem", color: "#6B7280" }}>Stage</Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: "none", pb: 1 }}>
                        <Typography sx={{ fontSize: "0.75rem", color: "#6B7280" }}>Date Entered</Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...stageHistory]
                      .sort(
                        (a, b) =>
                          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
                      )
                      .map((entry, i) => (
                        <TableRow
                          key={`${entry.value}-${i}`}
                          sx={{
                            "&:nth-of-type(even)": { bgcolor: "#F9FAFB" },
                            "&:last-child td": { borderBottom: 0 },
                          }}
                        >
                          <TableCell>
                            <Typography sx={{ fontSize: "0.875rem", color: "#111827" }}>
                              {resolveStage(entry.value)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: "0.875rem", color: "#111827" }}>
                              {formatDate(entry.timestamp)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          )}
        </>
      )}
    </Box>
  );
}
